// ANGEL FLIGHT — safe versioned save layer.
//
// All persistence funnels through here. Every read is try/catch-wrapped and
// migration-aware: unknown / old / corrupt input always degrades to a sensible
// default, never throws, never crashes boot. A storage shim is injectable so
// node tests run without a DOM. Export/import string moves progress between
// devices without an account.

import { SAVE_VERSION } from './types';
import type {
  Difficulty,
  GameSettings,
  SaveStateV1,
  UpgradeId,
  UpgradeTier,
  WeaponId,
} from './types';
import { REG } from './registryKeys';
import { MISSIONS } from '../missions/index';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const UPGRADE_IDS: readonly UpgradeId[] = [
  'damage',
  'fireRate',
  'ammoCap',
  'reload',
  'hull',
  'autoRepair',
] as const;

const MAX_TIER = 5;

const DIFFICULTIES: readonly Difficulty[] = ['recruit', 'pilot', 'ace'] as const;

const WEAPON_IDS: readonly WeaponId[] = [
  'minigun',
  'autocannon',
  'railgun',
  'flamer',
] as const;

const STARTER_WEAPON: WeaponId = 'minigun';

function defaultSettings(): GameSettings {
  return { master: 0.85, sfx: 0.9, music: 0.6, screenshake: true, hitstop: true };
}

export function defaultSave(): SaveStateV1 {
  const upgrades = {} as Record<UpgradeId, UpgradeTier>;
  for (const id of UPGRADE_IDS) upgrades[id] = 0;
  return {
    version: SAVE_VERSION,
    difficulty: 'pilot',
    scrip: 0,
    salvage: 0,
    equippedWeapon: STARTER_WEAPON,
    ownedWeapons: [STARTER_WEAPON],
    upgrades,
    missionProgress: 0,
    bestScores: {},
    settings: defaultSettings(),
    introSeen: false,
  };
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;
const asNum = (v: unknown, fb: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fb;
const asBool = (v: unknown, fb: boolean): boolean =>
  typeof v === 'boolean' ? v : fb;
const asUnit = (v: unknown, fb: number): number => {
  const n = asNum(v, fb);
  return n < 0 ? 0 : n > 1 ? 1 : n;
};
const clampInt = (v: unknown, lo: number, hi: number, fb: number): number => {
  const n = Math.floor(asNum(v, fb));
  return n < lo ? lo : n > hi ? hi : n;
};

export function migrateSave(raw: unknown): SaveStateV1 {
  if (!isObj(raw)) return defaultSave();
  if (raw['version'] !== SAVE_VERSION) return defaultSave();

  const base = defaultSave();

  const diff = raw['difficulty'];
  if (typeof diff === 'string' && (DIFFICULTIES as readonly string[]).includes(diff)) {
    base.difficulty = diff as Difficulty;
  }

  base.scrip = Math.max(0, Math.floor(asNum(raw['scrip'], 0)));
  base.salvage = Math.max(0, Math.floor(asNum(raw['salvage'], 0)));
  // Clamp to the registered mission count: a poisoned/legacy save can never
  // show missions as unlocked-and-cleared beyond the campaign that exists.
  base.missionProgress = clampInt(raw['missionProgress'], 0, MISSIONS.length, 0);

  const owned = new Set<WeaponId>([STARTER_WEAPON]);
  const rawOwned = raw['ownedWeapons'];
  if (Array.isArray(rawOwned)) {
    for (const w of rawOwned) {
      if (typeof w === 'string' && (WEAPON_IDS as readonly string[]).includes(w)) {
        owned.add(w as WeaponId);
      }
    }
  }
  base.ownedWeapons = [...owned];

  const eq = raw['equippedWeapon'];
  base.equippedWeapon =
    typeof eq === 'string' && owned.has(eq as WeaponId)
      ? (eq as WeaponId)
      : STARTER_WEAPON;

  const rawUp = raw['upgrades'];
  if (isObj(rawUp)) {
    for (const id of UPGRADE_IDS) {
      base.upgrades[id] = clampInt(rawUp[id], 0, MAX_TIER, 0) as UpgradeTier;
    }
  }

  const rawScores = raw['bestScores'];
  if (isObj(rawScores)) {
    for (const [k, v] of Object.entries(rawScores)) {
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
        base.bestScores[k] = Math.floor(v);
      }
    }
  }

  const rawSet = raw['settings'];
  if (isObj(rawSet)) {
    const d = base.settings;
    base.settings = {
      master: asUnit(rawSet['master'], d.master),
      sfx: asUnit(rawSet['sfx'], d.sfx),
      music: asUnit(rawSet['music'], d.music),
      screenshake: asBool(rawSet['screenshake'], d.screenshake),
      hitstop: asBool(rawSet['hitstop'], d.hitstop),
    };
  }

  // Additive field (same SAVE_VERSION): old saves default to false, so an
  // existing player sees the intro once and then never again.
  base.introSeen = asBool(raw['introSeen'], false);

  return base;
}

function resolveStorage(injected?: StorageLike): StorageLike | null {
  if (injected) return injected;
  try {
    const ls = (globalThis as { localStorage?: StorageLike }).localStorage;
    return ls ?? null;
  } catch {
    return null;
  }
}

export function loadSave(storage?: StorageLike): SaveStateV1 {
  const store = resolveStorage(storage);
  if (!store) return defaultSave();
  try {
    const raw = store.getItem(REG.SAVE);
    if (raw === null) return defaultSave();
    return migrateSave(JSON.parse(raw) as unknown);
  } catch {
    return defaultSave();
  }
}

export function writeSave(s: SaveStateV1, storage?: StorageLike): void {
  const store = resolveStorage(storage);
  if (!store) return;
  try {
    store.setItem(REG.SAVE, JSON.stringify(s));
  } catch {
    /* best-effort */
  }
}

export function exportSave(s: SaveStateV1): string {
  const json = JSON.stringify(s);
  if (typeof btoa === 'function') return btoa(unescape(encodeURIComponent(json)));
  return Buffer.from(json, 'utf-8').toString('base64');
}

export function importSave(code: string): SaveStateV1 | null {
  try {
    const json =
      typeof atob === 'function'
        ? decodeURIComponent(escape(atob(code)))
        : Buffer.from(code, 'base64').toString('utf-8');
    const parsed = JSON.parse(json) as unknown;
    if (!isObj(parsed)) return null;
    return migrateSave(parsed);
  } catch {
    return null;
  }
}
